import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, User } from 'lucide-react';
import { SlideOver } from '@/components/SlideOver.js';
import { CustomerForm, type CustomerFormValues } from '@/pages/customers/CustomerForm.js';
import {
  createCustomer,
  listCustomers,
  type CustomerListItem,
} from '@/lib/api/customers.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { usePosCartStore } from '@/stores/pos-cart.js';

type PosCustomerPickerProps = {
  /** compact = cart header strip; card = right panel empty state */
  variant?: 'compact' | 'card';
};

function toCartCustomer(row: CustomerListItem | Record<string, unknown>) {
  const base = {
    id: String(row.id),
    firstName: String(row.firstName ?? ''),
    lastName: (row.lastName as string | null) ?? null,
    phone: (row.phone as string | null) ?? null,
    email: (row.email as string | null) ?? null,
    lastVisitAt: (row.lastVisitAt as string | null) ?? null,
    customerStatus: (row as CustomerListItem).customerStatus ?? null,
    statusReason: (row as CustomerListItem).statusReason ?? null,
  };
  return {
    ...base,
    loyaltyPointsBalance: (row.loyaltyPointsBalance as number | string | null | undefined) ?? null,
    storeCreditBalance: (row.storeCreditBalance as number | string | null | undefined) ?? null,
  };
}

export function PosCustomerPicker({ variant = 'compact' }: PosCustomerPickerProps) {
  const queryClient = useQueryClient();
  const customer = usePosCartStore((s) => s.customer);
  const setCustomer = usePosCartStore((s) => s.setCustomer);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [focused, setFocused] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const customerQuery = useQuery({
    queryKey: ['pos-customer-search', debouncedSearch],
    queryFn: () => listCustomers({ q: debouncedSearch, page: 1, pageSize: 8 }),
    enabled: debouncedSearch.length >= 2,
  });

  const createMutation = useMutation({
    mutationFn: (values: CustomerFormValues) => createCustomer(values),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setCustomer(toCartCustomer(created));
      setCreateOpen(false);
      setSearch('');
      toastSuccess('Customer added to sale');
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  function selectCustomer(row: CustomerListItem) {
    setCustomer(toCartCustomer(row));
    setSearch('');
    setFocused(false);
  }

  const showDropdown =
    focused && (debouncedSearch.length >= 2 || search.trim().length > 0);

  if (variant === 'compact' && customer) {
    return null;
  }

  return (
    <>
      <div className={variant === 'card' ? 'space-y-3' : ''}>
        {variant === 'card' ? (
          <>
            <div className="font-bold text-slate-950">👤 No Customer Selected</div>
            <p className="text-sm text-slate-500">Walk-in sale, or search for an existing customer</p>
          </>
        ) : null}

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className={[
              'w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-primary',
              variant === 'card' ? 'mt-1' : '',
            ].join(' ')}
            placeholder="Search name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => window.setTimeout(() => setFocused(false), 150)}
          />

          {showDropdown ? (
            <div className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
              <button
                type="button"
                className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2.5 text-left text-sm hover:bg-slate-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setCustomer(null);
                  setSearch('');
                  setFocused(false);
                }}
              >
                <User className="h-4 w-4 text-slate-400" />
                <span className="font-semibold">Walk-in Customer</span>
              </button>

              {customerQuery.isFetching ? (
                <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
              ) : null}

              {debouncedSearch.length >= 2 && !customerQuery.isFetching && !customerQuery.data?.data.length ? (
                <div className="px-3 py-2 text-xs text-slate-500">No customers found</div>
              ) : null}

              {(customerQuery.data?.data ?? []).map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className="flex w-full flex-col px-3 py-2.5 text-left text-sm hover:bg-slate-50"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectCustomer(row)}
                >
                  <span className="font-semibold text-slate-950">
                    {row.firstName} {row.lastName ?? ''}
                  </span>
                  <span className="text-xs text-slate-500">
                    {[row.phone, row.email].filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))}

              {debouncedSearch.length < 2 ? (
                <div className="px-3 py-2 text-xs text-slate-400">Type at least 2 characters to search</div>
              ) : null}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className={[
            'inline-flex w-full items-center justify-center gap-2 rounded-lg font-semibold',
            variant === 'card'
              ? 'bg-primary px-3 py-2 text-sm text-white hover:bg-primary/90'
              : 'border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50',
          ].join(' ')}
        >
          <Plus className="h-4 w-4" />
          New Customer
        </button>
      </div>

      <SlideOver title="New Customer" open={createOpen} onClose={() => setCreateOpen(false)}>
        <CustomerForm
          submitLabel="Add to Sale"
          loading={createMutation.isPending}
          onSubmit={(values) => createMutation.mutate(values)}
        />
      </SlideOver>
    </>
  );
}
